import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { TutorialManagementHandler, TutorialTranslations } from './TutorialManagementHandler';

interface TutorialFormModalProps {
    tutorialId: string | null; // null = create mode
    onClose: () => void;
    onSaved: () => void;
}

const LANGUAGES: { code: string; name: string }[] = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'bn', name: 'বাংলা' },
    { code: 'pt', name: 'Português' },
    { code: 'it', name: 'Italiano' },
    { code: 'ru', name: 'Русский' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
];

// Fixed set of sections the public site renders. The first four are the Tutorials
// modal tabs; the last two are the standalone Beginners-Guide videos.
const CATEGORIES: { value: string; label: string }[] = [
    { value: 'introduction', label: 'Tutorials: Introduction' },
    { value: 'getting-started', label: 'Tutorials: Getting Started' },
    { value: 'vs-friends', label: 'Tutorials: Vs Friends' },
    { value: 'how-to-play', label: 'Tutorials: How to Play' },
    { value: 'beginners-basics', label: 'Beginners Guide: Sudoku Basics' },
    { value: 'beginners-rules', label: 'Beginners Guide: Game Rules' },
];

const emptyTranslations = (): TutorialTranslations =>
    LANGUAGES.reduce((acc, l) => ({ ...acc, [l.code]: '' }), {} as TutorialTranslations);

// Drop blank language entries so only filled translations are stored (keeps the
// list view's language count accurate and avoids persisting empty strings).
const pruneEmpty = (dict: TutorialTranslations): TutorialTranslations =>
    Object.fromEntries(Object.entries(dict).filter(([, v]) => v && v.trim() !== ''));

const TutorialFormModal: React.FC<TutorialFormModalProps> = ({ tutorialId, onClose, onSaved }) => {
    const { theme } = useTheme();
    const isEdit = tutorialId !== null;

    const [title, setTitle] = useState<TutorialTranslations>(emptyTranslations());
    const [videoUrl, setVideoUrl] = useState('');
    const [category, setCategory] = useState('introduction');
    const [priority, setPriority] = useState('1');
    const [active, setActive] = useState(true);

    const [activeLang, setActiveLang] = useState('en');
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isEdit || !tutorialId) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const handler = new TutorialManagementHandler();
                const d = await handler.getTutorialDetails(tutorialId);
                setTitle({ ...emptyTranslations(), ...(d.title || {}) });
                setVideoUrl(d.videoUrl || '');
                setCategory(d.category || 'introduction');
                setPriority(String(d.priority ?? 1));
                setActive(d.active ?? true);
            } catch (err) {
                console.error('Error loading tutorial video:', err);
                setError('Failed to load tutorial video.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isEdit, tutorialId]);

    const setLangValue = (lang: string, value: string) => {
        setTitle((prev) => ({ ...prev, [lang]: value }));
    };

    const handleSave = async () => {
        if (!title.en?.trim()) {
            setError('An English title is required.');
            setActiveLang('en');
            return;
        }
        if (!videoUrl.trim()) {
            setError('A video URL is required.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const handler = new TutorialManagementHandler();
            const payload = {
                title: pruneEmpty(title),
                videoUrl: videoUrl.trim(),
                category,
                priority: parseInt(priority, 10) || 0,
                active,
            };
            if (isEdit && tutorialId) {
                await handler.updateTutorial(tutorialId, payload);
            } else {
                await handler.createTutorial(payload);
            }
            onSaved();
        } catch (err) {
            console.error('Error saving tutorial video:', err);
            setError('Failed to save tutorial video. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '640px',
            width: '92%',
            maxHeight: '88vh',
            overflowY: 'auto' as const,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
        },
        title: { fontSize: '20px', fontWeight: 700, color: theme.text },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '22px',
            color: theme.textSecondary,
            cursor: 'pointer',
            padding: '4px 8px',
        },
        sectionTitle: {
            fontSize: '13px',
            fontWeight: 700,
            color: theme.textSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
            margin: '8px 0 12px',
        },
        grid2: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
        },
        fieldGroup: { marginBottom: '14px' },
        label: {
            display: 'block',
            fontSize: '13px',
            fontWeight: 600,
            color: theme.text,
            marginBottom: '6px',
        },
        input: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
        },
        tabRow: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: '6px',
            marginBottom: '14px',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.border}`,
        },
        tab: {
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '6px',
            cursor: 'pointer',
            border: `1px solid ${theme.border}`,
            background: 'transparent',
            color: theme.textSecondary,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
        },
        tabActive: {
            background: '#3b82f6',
            color: '#fff',
            border: '1px solid #3b82f6',
        },
        filledDot: {
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'inline-block',
        },
        hint: { fontSize: '11px', color: theme.textSecondary, marginTop: '4px' },
        divider: { height: '1px', background: theme.border, margin: '8px 0 18px' },
        checkboxRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
        checkbox: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#3b82f6' },
        footer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '8px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.border}`,
        },
        cancelButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.text,
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            cursor: 'pointer',
        },
        saveButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            opacity: saving ? 0.6 : 1,
        },
        errorText: { color: '#ef4444', fontSize: '14px', marginBottom: '12px' },
        spinner: {
            display: 'inline-block',
            width: '16px',
            height: '16px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <span style={styles.title}>{isEdit ? 'Edit Tutorial Video' : 'Add Tutorial Video'}</span>
                    <button style={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                {error && <div style={styles.errorText}>{error}</div>}

                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
                        <div style={styles.spinner} />
                        <span style={{ color: theme.textSecondary, fontSize: '13px' }}>Loading tutorial video...</span>
                    </div>
                ) : (
                    <>
                        <div style={styles.sectionTitle}>Settings</div>
                        <div style={styles.fieldGroup}>
                            <label htmlFor="tut-video" style={styles.label}>Video URL</label>
                            <input
                                id="tut-video"
                                type="text"
                                placeholder="https://youtube.com/shorts/..."
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                                style={styles.input}
                            />
                            <div style={styles.hint}>A YouTube link (shorts, watch, embed or youtu.be). The video id is extracted automatically.</div>
                        </div>
                        <div style={styles.grid2}>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="tut-category" style={styles.label}>Section</label>
                                <select
                                    id="tut-category"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    style={styles.input}
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="tut-priority" style={styles.label}>Priority (lower shows first)</label>
                                <input id="tut-priority" type="number" min="0" value={priority} onChange={(e) => setPriority(e.target.value)} style={styles.input} />
                            </div>
                        </div>

                        <div style={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                                style={styles.checkbox}
                                id="tut-active"
                            />
                            <label htmlFor="tut-active" style={{ ...styles.label, marginBottom: 0, cursor: 'pointer' }}>
                                Active (visible on the website)
                            </label>
                        </div>

                        <div style={styles.divider} />

                        <div style={styles.sectionTitle}>Localized Title</div>
                        <div style={styles.tabRow}>
                            {LANGUAGES.map((l) => (
                                <button
                                    key={l.code}
                                    type="button"
                                    style={{ ...styles.tab, ...(activeLang === l.code ? styles.tabActive : {}) }}
                                    onClick={() => setActiveLang(l.code)}
                                >
                                    {l.name}
                                    {title[l.code]?.trim() ? <span style={styles.filledDot} /> : null}
                                </button>
                            ))}
                        </div>

                        <div style={styles.fieldGroup}>
                            <label htmlFor="tut-title" style={styles.label}>Title ({activeLang})</label>
                            <input
                                id="tut-title"
                                type="text"
                                value={title[activeLang] || ''}
                                onChange={(e) => setLangValue(activeLang, e.target.value)}
                                style={styles.input}
                            />
                        </div>
                    </>
                )}

                <div style={styles.footer}>
                    <button style={styles.cancelButton} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button style={styles.saveButton} onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving...' : isEdit ? 'Update Video' : 'Add Video'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TutorialFormModal;
